// lib/issueStage.mjs — R123 (Jesse: "it's very hard to keep tabs on what's
// been triaged, what's been actioned … issues that have a WO assigned, but
// that's still logged as open … we need another status that accounts for
// that"). The Issues board keeps `status` exactly as it is (open |
// in_progress | resolved | dismissed — boardRowStatus, findingState, the EOD
// digest, the calendar health layer and Jarvis all read it) and DERIVES a
// pipeline `stage` at read time from facts the board already has. Shared by
// GET /issues/board (lazy dynamic import, like wcLib) and the browser
// (client/src/pages/issues/stage.ts), so the two never disagree. Pure ESM, no
// DOM, no I/O: `node lib/issueStage.test.mjs` runs the same code the client
// bundles.
//
// stageOf(row, facts) → { stage, lane, why, date, flags[], who }
// THE LADDER (first match wins; `today` is the Pacific business day):
//   1. status dismissed → dismissed; status resolved → done
//   2. row.wo.chip 'unknown' (the WO store could not be read) → unknown / wo_unreadable
//   3. a LIVE WO decides (facts.dispatch = lib/woCalendar woDispatch(), present
//      for a wo: row whose WO is not done, or an issue row whose WO chip is not
//      missing / unknown / done):
//        a. vendor says done / every item ticked → verify / vendor_done | all_ticked
//        b. WO in progress / timer running      → in_work / on_site | timer   (date = booked day)
//        c. nobody (no person, no vendor)       → no_owner / no_day | booked | was_booked (date = booked day)
//        d. no booked day                       → no_date / no_day | followup
//        e. booked span ended before today      → scheduled / missed (flag missed; date = booked start)
//        f. otherwise                           → scheduled / booked (date = booked start)
//      flags on any WO stage: waiting_owner, quote_pending, followup, overdue, due_soon, urgent
//   4. row.wo.chip 'missing' (the WO was deleted) → triaged / route_gone
//   5. routed (routedOf: the board overlay's `routed`, the supply hand-off, or
//      the Log Issues page's own rec.routed):
//        task       missing / canceled → triaged / route_gone; in_progress / blocked → in_work / task;
//                   a due date → scheduled / task_due (flag missed when past); else handed_off / task
//        recovery   missing → triaged / route_gone; else handed_off / recovery
//        supply     handed_off / supply
//        clean      turn missing → triaged / route_gone; turn done → verify / clean_done;
//                   checkout before today → verify / clean_passed; turn in progress → in_work / cleaning;
//                   else scheduled / next_clean (date = checkout, who = cleaner)
//        inspection handed_off / next_inspection (no date: follow-through is not reliable, R123 §0.5)
//   6. a triage stamp (ao_issue_triage) → triaged / stamp
//   7. record status in_progress with no destination (incident, legacy quick-log) → in_work / record
//   8. otherwise new / review_signal (review rows) | new
// Flags on any live row: asked, new_info (the info block), urgent (severity
// emergency / high), review_signal. Terminal rows carry no flags.
//
// Invariants (lib/issueStage.test.mjs): done ⇔ resolved, dismissed ⇔
// dismissed, every live row has a live stage, lane counts sum to the rows.
import { laToday } from './woFormat.mjs';
import { diffDays } from './woCalendar.mjs';

export const STAGES = Object.freeze(['new', 'triaged', 'no_owner', 'no_date', 'scheduled', 'verify', 'in_work', 'handed_off', 'unknown', 'done', 'dismissed']);
export const TERMINAL_STAGES = Object.freeze(['done', 'dismissed']);
export const LANES = Object.freeze(['triage', 'schedule', 'scheduled', 'verify', 'in_hand', 'closed']);
/** The five lanes a person works (the lane cards); 'closed' holds done + dismissed. */
export const LIVE_LANES = Object.freeze(['triage', 'schedule', 'scheduled', 'verify', 'in_hand']);
export const LANE_OF = Object.freeze({
  new: 'triage',
  triaged: 'schedule', no_owner: 'schedule', no_date: 'schedule',
  scheduled: 'scheduled',
  verify: 'verify',
  in_work: 'in_hand', handed_off: 'in_hand', unknown: 'in_hand',
  done: 'closed', dismissed: 'closed',
});
export const LANE_LABEL = Object.freeze({ triage: 'To triage', schedule: 'To schedule', scheduled: 'Scheduled', verify: 'Check & close', in_hand: 'With others' });
/** The stage pill's base words (the board adds the date: "Booked Thu Oct 23", "Missed Oct 20", "Next clean Fri Oct 24"). */
export const STAGE_LABEL = Object.freeze({
  new: 'New',
  triaged: 'Triaged',
  no_owner: 'On WO · nobody assigned',
  no_date: 'Assigned · no day',
  scheduled: 'Scheduled',
  verify: "Check it's fixed",
  in_work: 'In progress',
  handed_off: 'With others',
  unknown: 'WO unreadable',
  done: 'Done',
  dismissed: 'Dismissed',
});
/** Why a row sits in its stage (the fact line / tooltip words). */
export const WHY_LABEL = Object.freeze({
  resolved: 'Resolved',
  dismissed: 'Dismissed',
  wo_unreadable: 'WO unreadable',
  vendor_done: 'Vendor says done',
  all_ticked: 'Every WO item ticked',
  timer: 'Timer running',
  on_site: 'WO in progress',
  no_day: 'No day booked',
  booked: 'Booked',
  was_booked: 'Booked day passed',
  followup: 'Follow-up needed',
  missed: 'Missed booking',
  route_gone: 'Re-assign',
  task: 'Task',
  task_due: 'Task due',
  recovery: 'With Guest Recovery',
  supply: 'Supply request',
  clean_done: 'Clean done',
  clean_passed: 'Clean day passed',
  cleaning: 'Cleaning now',
  next_clean: 'Next clean',
  next_inspection: 'With next inspection',
  stamp: 'Triaged',
  record: 'In progress',
  review_signal: 'Review theme',
  new: 'New',
});
/** Flags, in the order stageOf emits them. */
export const FLAGS = Object.freeze(['urgent', 'missed', 'was_booked', 'overdue', 'due_soon', 'waiting_owner', 'quote_pending', 'followup', 'asked', 'new_info', 'review_signal']);
export const FLAG_LABEL = Object.freeze({
  urgent: 'Urgent',
  missed: 'Missed',
  was_booked: 'Booked day passed',
  overdue: 'Overdue',
  due_soon: 'Due soon',
  waiting_owner: 'Waiting on owner',
  quote_pending: 'Quote pending',
  followup: 'Follow-up needed',
  asked: 'Asked for info',
  new_info: 'New info',
  review_signal: 'Review theme',
});
/** ao_turns statuses that mean the clean happened (server/assetops.js TURN_DONE). */
export const TURN_DONE = Object.freeze(['cleaned', 'verified', 'ready']);
export const ROUTE_KINDS = Object.freeze(['task', 'recovery', 'supply', 'clean', 'inspection']);

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const isObj = (x) => !!x && typeof x === 'object' && !Array.isArray(x);
const str = (v) => (v == null ? '' : String(v));
const dayOf = (v) => { const s = str(v).slice(0, 10); return DAY.test(s) ? s : null; };
const clip = (v, n) => { const s = str(v).trim(); return s ? s.slice(0, n) : null; };

/** The Pacific business day (YYYY-MM-DD) of a moment (default: now) — the server's bizToday(). */
export function bizDay(now) {
  return laToday(now == null ? undefined : (now instanceof Date ? now : new Date(now)));
}
/** The Pacific calendar day of a timestamp; a bare YYYY-MM-DD is already a day (never shifted). */
export function bizDayOf(iso) {
  const s = str(iso);
  if (!s) return null;
  if (DAY.test(s)) return s;
  const t = Date.parse(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s) ? s.replace(' ', 'T') + 'Z' : s);
  return Number.isFinite(t) ? bizDay(new Date(t)) : null;
}
/** The To-triage band of a row that came in at `iso`: 'today' | 'week' (1–6 days) | 'older' (7+ days, or no date). */
export function ageBandOf(iso, today) {
  const d = bizDayOf(iso), t = dayOf(today) || bizDay();
  if (!d) return 'older';
  const n = diffDays(d, t);
  if (n == null) return 'older';
  return n <= 0 ? 'today' : n < 7 ? 'week' : 'older';
}
export const isLiveStage = (s) => STAGES.includes(s) && !TERMINAL_STAGES.includes(s);

/**
 * The WO id whose woDispatch() a row's stage needs, or null: a wo: row (its own WO; the caller still
 * checks the WO exists and is not done), or an issue row whose WO chip is not missing / unknown / done.
 */
export function dispatchWoIdOf(row) {
  if (!isObj(row)) return null;
  if (row.source === 'maintenance') return row.source_id != null && str(row.source_id) ? str(row.source_id) : null;
  const w = row.wo;
  if (!isObj(w) || !w.id || ['missing', 'unknown', 'done'].includes(str(w.chip))) return null;
  return str(w.id);
}

// ── routedOf: the row's ONE destination, normalized ─────────────────────────
// → { kind, ref_id, at, via: 'board'|'log', auto?, status, date, due_date, who } | null
//   status   the destination's live status where the board follows it: a task's
//            status, a recovery case's recovery_status, a clean's turn status;
//            'missing' when the record is gone; null when unknown (a failed
//            strict read never reads as "gone").
//   date     a clean's checkout day; due_date a task's due day; who the
//            cleaner / task assignee.
const routeStatusOf = (rt) => {
  const v = rt.target_status ?? (rt.kind === 'task' ? rt.task_status : rt.kind === 'recovery' ? rt.recovery_status : rt.kind === 'clean' ? rt.turn_status : undefined);
  return v == null || v === '' ? null : str(v);
};
function normRoute(rt, via) {
  if (!isObj(rt) || !ROUTE_KINDS.includes(str(rt.kind))) return null;
  const out = {
    kind: str(rt.kind),
    ref_id: rt.ref_id != null && str(rt.ref_id) ? str(rt.ref_id) : null,
    at: rt.at ? str(rt.at) : null,
    via: rt.via === 'log' ? 'log' : via,
    status: routeStatusOf(rt),
    date: dayOf(rt.date ?? rt.check_out),
    due_date: dayOf(rt.due_date),
    who: clip(rt.who ?? rt.assignee, 80),
  };
  if (rt.auto) out.auto = true;
  return out;
}
const isGone = (rt) => !!rt && ((rt.kind === 'task' && (rt.status === 'missing' || rt.status === 'canceled')) || ((rt.kind === 'recovery' || rt.kind === 'clean') && rt.status === 'missing'));
const hasWoLink = (row, rec) => !!(isObj(row.wo) || row.wo_link || row.wo_link_id || row.wo_id || (rec && rec.wo_id));
// The Log Issues page's record routing ({ wo_id?, clean_turn_id?, focus_key?, supply_order_id?, claim_id? })
// — a server row carries it as log_routed (+ at); a raw ao_logged_issues record as rec.routed (no kind).
function logRoute(row) {
  const rec = isObj(row.log_routed) ? row.log_routed : (isObj(row.routed) && !row.routed.kind ? row.routed : null);
  if (!rec || hasWoLink(row, rec)) return null;
  const at = rec.at || row.updated_at || null;
  if (rec.clean_turn_id) return normRoute({ kind: 'clean', ref_id: rec.clean_turn_id, at, via: 'log', target_status: rec.target_status, date: rec.date, who: rec.who }, 'log');
  if (rec.focus_key) return normRoute({ kind: 'inspection', ref_id: rec.focus_key, at, via: 'log' }, 'log');
  if (rec.supply_order_id) return normRoute({ kind: 'supply', ref_id: rec.supply_order_id, at, via: 'log' }, 'log');
  return null;
}
function supplyRoute(row) {
  if (!row.routed_supply) return null;
  const id = typeof row.routed_supply === 'string' ? row.routed_supply : (isObj(row.supply_hint) && row.supply_hint.order_id) || null;
  return normRoute({ kind: 'supply', ref_id: id }, 'board');
}
/** The row's destination: the first LIVE one of (board route, supply hand-off, Log Issues route), else a dead one (→ re-assign), else null. */
export function routedOf(row) {
  if (!isObj(row)) return null;
  const board = isObj(row.routed) && row.routed.kind ? normRoute(row.routed, 'board') : null;
  const cands = [board, supplyRoute(row), logRoute(row)].filter(Boolean);
  return cands.find(rt => !isGone(rt)) || cands[0] || null;
}

// ── the ladder ───────────────────────────────────────────────────────────────
const URGENT_SEV = new Set(['emergency', 'high']);
const liveDispatch = (row, d) => {
  if (!isObj(d)) return null;
  if (d.status === 'done' || d.status === 'cancelled') return null;
  if (isObj(row.wo) && ['missing', 'unknown', 'done', 'item_done'].includes(str(row.wo.chip))) return null;
  return d;
};

/**
 * @param row   a board row ({ id, source, status, severity, wo?, routed?, routed_supply?, log_routed?, triaged?, info?, dispatch? })
 * @param facts { today: 'YYYY-MM-DD' (Pacific), now?, dispatch?, routed?, triaged?, info? } — an explicit fact
 *              (even null) overrides the row's own field; today falls back to bizDay(facts.now).
 */
export function stageOf(row, facts) {
  const r = isObj(row) ? row : {};
  const f = isObj(facts) ? facts : {};
  const today = dayOf(f.today) || bizDay(f.now);
  const flags = new Set();
  const emit = (stage, why, date = null, who = null) => {
    const live = !TERMINAL_STAGES.includes(stage);
    if (live) {
      const info = f.info !== undefined ? f.info : r.info;
      if (isObj(info) && Number(info.open) > 0) flags.add('asked');
      if (isObj(info) && Number(info.unread) > 0) flags.add('new_info');
      if (URGENT_SEV.has(str(r.severity))) flags.add('urgent');
      if (r.source === 'review') flags.add('review_signal');
    }
    return { stage, lane: LANE_OF[stage], why, date: dayOf(date), flags: live ? FLAGS.filter(k => flags.has(k)) : [], who: who ? clip(who, 80) : null };
  };

  // 1. terminal statuses (status is never re-derived here)
  if (r.status === 'dismissed') return emit('dismissed', 'dismissed');
  if (r.status === 'resolved') return emit('done', 'resolved');
  // 2. the WO store could not be read
  if (isObj(r.wo) && r.wo.chip === 'unknown') return emit('unknown', 'wo_unreadable');
  // 3. a live WO decides
  const d = liveDispatch(r, f.dispatch !== undefined ? f.dispatch : r.dispatch);
  if (d) {
    if (d.owner_state === 'needs_approval') flags.add('waiting_owner');
    if (d.quote_pending) flags.add('quote_pending');
    if (d.followup) flags.add('followup');
    if (d.overdue) flags.add('overdue');
    else if (d.due_soon) flags.add('due_soon');
    if (d.urgent) flags.add('urgent');
    const booked = dayOf(d.booked);
    const bookedEnd = dayOf(d.booked_end) || booked;
    const who = d.who || null;
    if ((isObj(r.wo) && r.wo.chip === 'vendor_done') || d.vendor_done) return emit('verify', 'vendor_done', booked, who);
    if (d.all_ticked) return emit('verify', 'all_ticked', booked, who);
    if (d.timer_on) return emit('in_work', 'timer', booked, who);
    if (d.status === 'in_progress') return emit('in_work', 'on_site', booked, who);
    const nobody = !str(d.assigned_to).trim() && !(d.vendor_id != null && str(d.vendor_id));
    if (nobody) {
      if (!booked) return emit('no_owner', 'no_day');
      if (bookedEnd < today) { flags.add('was_booked'); return emit('no_owner', 'was_booked', booked); }
      return emit('no_owner', 'booked', booked);
    }
    if (!booked) return emit('no_date', d.followup ? 'followup' : 'no_day', null, who);
    if (bookedEnd < today) { flags.add('missed'); return emit('scheduled', 'missed', booked, who); }
    return emit('scheduled', 'booked', booked, who);
  }
  // 4. the WO was deleted
  if (isObj(r.wo) && r.wo.chip === 'missing') return emit('triaged', 'route_gone');
  // 5. routed somewhere else
  const rt = f.routed !== undefined ? (isObj(f.routed) && f.routed.kind ? normRoute(f.routed, 'board') : null) : routedOf(r);
  if (rt) {
    if (rt.kind === 'task') {
      if (rt.status === 'missing' || rt.status === 'canceled') return emit('triaged', 'route_gone');
      if (rt.status === 'in_progress' || rt.status === 'blocked') return emit('in_work', 'task', rt.due_date, rt.who);
      if (rt.due_date) { if (rt.due_date < today) flags.add('missed'); return emit('scheduled', 'task_due', rt.due_date, rt.who); }
      return emit('handed_off', 'task', null, rt.who);
    }
    if (rt.kind === 'recovery') return rt.status === 'missing' ? emit('triaged', 'route_gone') : emit('handed_off', 'recovery');
    if (rt.kind === 'supply') return emit('handed_off', 'supply');
    if (rt.kind === 'clean') {
      if (rt.status === 'missing') return emit('triaged', 'route_gone');
      if (rt.status && TURN_DONE.includes(rt.status)) return emit('verify', 'clean_done', rt.date, rt.who);
      if (rt.date && rt.date < today) return emit('verify', 'clean_passed', rt.date, rt.who);
      if (rt.status === 'in_progress') return emit('in_work', 'cleaning', rt.date, rt.who);
      return emit('scheduled', 'next_clean', rt.date, rt.who);
    }
    if (rt.kind === 'inspection') return emit('handed_off', 'next_inspection');
  }
  // 6. a triage stamp
  const triaged = f.triaged !== undefined ? f.triaged : r.triaged;
  if (triaged) return emit('triaged', 'stamp');
  // 7. a record in progress with no destination
  if (r.status === 'in_progress') return emit('in_work', 'record');
  // 8. new
  return emit('new', r.source === 'review' ? 'review_signal' : 'new');
}

/** { by_stage, by_lane } over rows that carry .stage (rows without one are skipped). */
export function tallyStages(rows) {
  const by_stage = {}, by_lane = {};
  for (const r of (Array.isArray(rows) ? rows : [])) {
    if (!r || !STAGES.includes(r.stage)) continue;
    by_stage[r.stage] = (by_stage[r.stage] || 0) + 1;
    const lane = LANE_OF[r.stage];
    by_lane[lane] = (by_lane[lane] || 0) + 1;
  }
  return { by_stage, by_lane };
}
